import * as Sentry from '@sentry/react'
import cookie from 'js-cookie'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { logout } from '@/lib/hocs/withAuthSync'
import { useRestQuery } from '@/lib/hook/tanstackQuery'
import { urlSiteGroupement } from '@/lib/urlSiteGroupement'
import { ApiQueryEndpoint } from '@/src/ApiQueryEndpoint'
import LOCAL_STORAGE from '@/src/localstorage'
import { UsersProfile } from '@/type/profile'

type Return = {
  profile: UsersProfile | null
  loadingProfile: boolean
  dataLanguagesAvailable: Record<any, any>[]
  refetch: () => void
  isAuthenticated: boolean
}

// Langues supportees (statique, ex-resolver Apollo QUERIES.LOCAL.GET_LANGUAGES_AVAILABLE
// alimente depuis _app.tsx). Memes valeurs que la constante languagesAvailable de _app.tsx.
const LANGUAGES_AVAILABLE: Record<any, any>[] = [
  { lang_code: 'fr', name: 'French', country_code: 'FR', is_default: true },
  { lang_code: 'en', name: 'English', country_code: 'EN', is_default: false },
]

// Lecture du token API depuis le cookie (meme cle que getTokenFromCtx :
// pharmedistore_token, JSON { token, ttl, storefront_id, ... }). Cote serveur
// js-cookie renvoie undefined -> null (le profil est charge cote client).
const readTokenFromCookie = (): string | null => {
  const raw = cookie.get(LOCAL_STORAGE.name_cookie_token)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return parsed?.token ?? null
  } catch {
    return null
  }
}

// Reproduit le post-traitement de l'ancien resolver Apollo (getUsers.ts) :
// l'endpoint REST `profile` renvoie les usergroups bruts et amountMinUsePot non type.
const transformProfile = (raw: any): UsersProfile | null => {
  if (!raw) return null
  return {
    ...raw,
    amountMinUsePot:
      raw.amountMinUsePot !== undefined && raw.amountMinUsePot !== null
        ? Number(raw.amountMinUsePot)
        : 50,
    usergroupids: raw.usergroups
      ? Object.values(raw.usergroups)
          .filter((ug: any) => ug.status === 'A')
          .map((ug: any) => ug.usergroup_id)
      : (raw.usergroupids ?? []),
  } as UsersProfile
}

const useProfile = ({ host = null } = {}): Return => {
  const {
    siteCos,
    siteLesOfficinales,
    siteGcapPharma,
    siteCentraleMediprix,
    sitePharmacologyStore,
    siteCentraleDpharma,
    siteAeliaStore,
    siteOutremer,
    siteHelloPharmacie,
    siteNormandiePharma,
    site37Degres,
    storefrontId,
  } = urlSiteGroupement(host)
  const router = useRouter()
  const locale = (router.query.locale as string) || 'fr'

  // Token lu depuis le cookie cote client (remplace QUERIES.LOCAL.GET_AUTHENTICATE).
  // Initialise a null pour un rendu SSR/hydratation deterministe, puis hydrate
  // depuis le cookie au montage. Resynchronise sur changement de route (login/logout).
  const [token, setToken] = useState<string | null>(null)
  useEffect(() => {
    // Resync uniquement si la valeur change (evite un re-render a chaque navigation).
    const next = readTokenFromCookie()
    setToken(prev => (prev === next ? prev : next))
  }, [router.asPath])

  const languagesAvailable = LANGUAGES_AVAILABLE
  const loadingLanguagesAvailable = false

  const {
    data: profile = null,
    isLoading,
    isError,
    refetch,
  } = useRestQuery<any, Error, UsersProfile | null>({
    ...ApiQueryEndpoint.USERS.GET_PROFILE,
    queryKey: [ApiQueryEndpoint.USERS.GET_PROFILE.endpoint],
    enabled: !!token,
    retry: 1,
    select: transformProfile,
  })

  // Session expiree : le token existe mais le profil ne peut pas etre recupere.
  // Remplace l'ancien handler Apollo onError(getUsers => Unauthorized).
  useEffect(() => {
    if (token && isError) {
      toast.error('Votre session a expirée')
      logout({ locale })
    }
  }, [token, isError, locale])

  !!profile && Sentry.setUser({ email: profile.email })

  const profileParse: any = profile
  const groupCos = (siteCos && !profileParse) || profileParse?.groupCos
  const groupLesOfficinales =
    (siteLesOfficinales && !profileParse) || profileParse?.groupLesOfficinales
  const groupGcapPharma = (siteGcapPharma && !profileParse) || profileParse?.groupGcapPharma
  const groupCentraleMediprix =
    (siteCentraleMediprix && !profileParse) || profileParse?.groupCentraleMediprix
  const groupPharmacologyStore =
    (sitePharmacologyStore && !profileParse) || profileParse?.groupPharmacologyStore
  const groupCentraleDpharma =
    (siteCentraleDpharma && !profileParse) || profileParse?.groupCentraleDpharma
  const groupAeliaStore = (siteAeliaStore && !profileParse) || profileParse?.groupAeliaStore
  const groupOutreMer = (siteOutremer && !profileParse) || profileParse?.groupOutreMer
  const groupHelloPharmacie =
    (siteHelloPharmacie && !profileParse) || profileParse?.groupHelloPharmacie
  const groupNormandiePharma =
    (siteNormandiePharma && !profileParse) || profileParse?.groupNormandiePharma
  const group37Degres = (site37Degres && !profileParse) || profileParse?.group37Degres

  return {
    profile:
      profileParse === null || !token
        ? {
            groupCos,
            groupLesOfficinales,
            groupGcapPharma,
            groupCentraleMediprix,
            groupPharmacologyStore,
            groupCentraleDpharma,
            groupAeliaStore,
            groupOutreMer,
            groupHelloPharmacie,
            groupNormandiePharma,
            group37Degres,
            storefrontId,
          }
        : {
            ...profileParse?.billing,
            ...profileParse,
            groupCos,
            groupLesOfficinales,
            groupGcapPharma,
            groupCentraleMediprix,
            groupPharmacologyStore,
            groupCentraleDpharma,
            groupAeliaStore,
            groupOutreMer,
            groupHelloPharmacie,
            groupNormandiePharma,
            group37Degres,
          },
    loadingProfile: isLoading || loadingLanguagesAvailable,
    dataLanguagesAvailable: languagesAvailable,
    refetch: () => refetch(),
    isAuthenticated: !!token,
  }
}

export default useProfile
